package com.dotdrives.web.pages.drives

import com.dotdrives.controller.DrivesBoardController
import com.dotdrives.controller.DrivesBoardUiState
import com.dotdrives.model.DriveSummary
import react.FC
import react.Props
import react.dom.aria.AriaRole
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.option
import react.dom.html.ReactHTML.select
import react.dom.html.ReactHTML.p
import react.useState
import web.cssom.ClassName
import web.html.ButtonType
import web.html.InputType
import web.html.button as buttonType
import web.html.date
import kotlin.time.Clock
import kotlin.time.Duration.Companion.days

external interface ClosedHistoryDrawerProps : Props {
    var state: DrivesBoardUiState
    var controller: DrivesBoardController
    var onOpen: (DriveSummary) -> Unit
    var onClose: () -> Unit
}

val ClosedHistoryDrawer = FC<ClosedHistoryDrawerProps> { props ->
    com.dotdrives.web.ui.useDialogFocus(true, "drive-history-dialog", onClose = props.onClose)
    val board = props.state
    val defaultStart = (Clock.System.now() - 90.days).toString().take(10)
    val (period, setPeriod) = useState(when {
        board.historyStartDate == null && board.historyEndDate == null -> "all"
        board.historyStartDate == defaultStart && board.historyEndDate == null -> "90"
        else -> "custom"
    })
    val (start, setStart) = useState(board.historyStartDate.orEmpty())
    val (end, setEnd) = useState(board.historyEndDate.orEmpty())
    val invalid = start.isNotEmpty() && end.isNotEmpty() && start > end
    div {
        className = ClassName("drives-detail-backdrop drives-history-backdrop")
        onClick = { props.onClose() }
        div {
            className = ClassName("drives-detail-panel drives-history-panel")
            id = web.dom.ElementId("drive-history-dialog")
            role = "dialog".unsafeCast<AriaRole>()
            ariaModal = true
            ariaLabel = "Closed drive history"
            onClick = { it.stopPropagation() }
            div {
                className = ClassName("drives-detail-head")
                div { className = ClassName("drives-detail-title"); +"Closed history" }
                button { className = ClassName("drives-history-close"); type = ButtonType.buttonType; ariaLabel = "Close history"; onClick = { props.onClose() }; +"Close ×" }
                p { +"${board.templates.firstOrNull { it.id == board.selectedTemplateId }?.name ?: "Selected process"} · Archived and outcome-set drives" }
                select {
                    ariaLabel = "Closed history period"
                    value = period
                    onChange = {
                        val value = it.currentTarget.value
                        setPeriod(value)
                        when (value) {
                            "90" -> props.controller.setHistoryDates((Clock.System.now() - 90.days).toString().take(10), null)
                            "all" -> props.controller.setHistoryDates(null, null)
                        }
                    }
                    option { value = "90"; +"Last 90 days" }
                    option { value = "all"; +"All time" }
                    option { value = "custom"; +"Custom dates" }
                }
                if (period == "custom") {
                    input { type = InputType.date; ariaLabel = "Closed on or after (UTC)"; value = start; onChange = { setStart(it.currentTarget.value) } }
                    input { type = InputType.date; ariaLabel = "Closed on or before (UTC)"; value = end; onChange = { setEnd(it.currentTarget.value) } }
                    button { type = ButtonType.buttonType; disabled = invalid; onClick = { props.controller.setHistoryDates(start.ifBlank { null }, end.ifBlank { null }) }; +"Apply dates" }
                    if (invalid) p { +"End date must be on or after the start date." }
                }
                p { +"Dates use archive time, or terminal phase entry for other closed drives, in UTC. Migrated dates reflect the historical timestamps available." }
            }
            CompletedDrivesList {
                loading = board.completedLoading
                drives = board.completed
                financials = board.completedFinancials
                financialTotals = board.completedFinancialTotals
                page = board.completedPage
                totalPages = board.completedTotalPages
                totalCount = board.completedTotalCount
                error = board.configurationError ?: board.completedError
                onPage = props.controller::goToCompletedPage
                onRetry = props.controller::retry
                onOpen = props.onOpen
                onBackToActive = props.onClose
            }
        }
    }
}
